import React, { useEffect, useState } from "react";
import { Input, Select, Textarea } from "../../../ui";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { getStripePromise } from "../../../../utils/stripeClient";
import { generateReceiptNumber } from "../../../../utils/receiptNumber";
import { useTheme } from "../../../../context/ThemeContext";
import { getModalStyles, getPrimaryText, getSecondaryText, getInputStyles } from "../../../../utils/themeUtils";
import { PaymentMethodType, ACH_PROCESSING_MESSAGE } from "../../../../types/payment.types";
import { captureError } from "../../../../utils/sentry";
import ReaderPicker from "../../../payment/reader/ReaderPicker";
import ReceiptNumberWarning from "../../../payment/ReceiptNumberWarning";
import { TerminalReader } from "../../../../api/terminalAPI";

type SelectAdvancePaymentMethodModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    method: PaymentMethodType;
    amount: number;
    useSavedCard: boolean;
    useSavedBankAccount?: boolean;
    paymentDate?: string;
    readerId?: string | null;
    saveCardOnReader?: boolean;
    /** Shared accounts: the person on the account who paid. Absent when not chosen. */
    paidByMembershipUserId?: string;
    checkDetails?: {
      checkNumber: string;
      bankName: string;
      checkDate: string;
    };
  }) => Promise<boolean>;
  savedCardLast4?: string;
  savedCards?: any[];
  defaultPaymentMethodId?: string | null;
  onCardSelect?: (cardId: string) => void;
  savedBankAccountLast4?: string;
  savedBankAccountName?: string;
  /**
   * The membership's current amount due (positive number). Pre-fills the
   * Amount field when the modal opens so office staff collect to a zero
   * balance by default (Stacy/GoY request). Staff can freely edit it.
   */
  defaultAmount?: number;
  /** Card readers (Stripe Terminal). Absent or empty → no reader option. */
  readers?: TerminalReader[];
  readerId?: string | null;
  onReaderSelect?: (readerId: string) => void;
  saveCardOnReader?: boolean;
  onSaveCardOnReaderChange?: (v: boolean) => void;
  /**
   * The people on the account. With two or more, the form asks who paid
   * (in place of typing a name into the note). Fewer, and the field is not
   * drawn at all.
   */
  people?: { id: string; label: string }[];
  /** Who "Paid by" starts on: the person responsible for the open invoice. */
  defaultPaidById?: string;
  fetch: any;
  /** The family being paid for: lets the Receipt # field warn about a reused number. */
  membershipId?: string | null;
};

const SelectAdvancePaymentMethodInMember: React.FC<
  SelectAdvancePaymentMethodModalProps
> = ({ isOpen, onClose, onSubmit, savedCardLast4, savedCards = [], defaultPaymentMethodId, onCardSelect, savedBankAccountLast4, savedBankAccountName, defaultAmount, readers = [], readerId = null, onReaderSelect, saveCardOnReader = false, onSaveCardOnReaderChange, people = [], defaultPaidById = "", fetch, membershipId = null }) => {
  const { isDarkMode } = useTheme();
  const stripe = useStripe();
  const elements = useElements();
  const [method, setMethod] = useState<PaymentMethodType>("cash");
  const [amount, setAmount] = useState(0);
  // Whether staff have typed in the Amount field this session — once they
  // have, the amount-due prefill must never clobber their value.
  const [amountTouched, setAmountTouched] = useState(false);
  const [useSavedCard, setUseSavedCard] = useState(true);
  const [useSavedBankAccount, setUseSavedBankAccount] = useState(true);
  const [receiptNumber, setReceiptNumber] = useState();
  const [settings, setSettings] = useState<any>(null);

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // payment date field (defaults to today)
  const [paymentDate, setPaymentDate] = useState(getTodayDate());

  // Shared accounts: who paid. Starts on the responsible person each time the
  // form opens; once staff pick someone, a late-arriving default must not
  // overwrite their choice.
  const askWhoPaid = people.length >= 2;
  const [paidBy, setPaidBy] = useState("");
  const [paidByTouched, setPaidByTouched] = useState(false);
  useEffect(() => {
    if (!isOpen) {
      setPaidByTouched(false);
      return;
    }
    if (!paidByTouched) setPaidBy(defaultPaidById || "");
  }, [isOpen, defaultPaidById, paidByTouched]);

  // Reset payment date to today when switching from Card/Bank to other methods
  useEffect(() => {
    if (method !== "card" && method !== "us_bank_account") {
      setPaymentDate(getTodayDate());
    }
  }, [method]);

  // Pre-fill the Amount with the membership's amount due when the modal
  // opens (Stacy/GoY: "can the total amount due automatically come up and
  // already be there?"). Only when the field is untouched and still empty,
  // and only for a real amount due (> 0) — a zero/credit balance leaves the
  // field alone. `defaultAmount` is also in the deps because the balance can
  // arrive async after the modal is already open.
  useEffect(() => {
    if (!isOpen || amountTouched) return;
    if (typeof defaultAmount !== "number" || !(defaultAmount > 0)) return;
    setAmount((prev) => (prev > 0 ? prev : Math.round(defaultAmount * 100) / 100));
  }, [isOpen, defaultAmount, amountTouched]);

  // check fields
  const [checkNumber, setCheckNumber] = useState("");
  const [bankName, setBankName] = useState("");
  const [checkDate, setCheckDate] = useState("");
  // note field
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Generate receipt number when modal opens
  useEffect(() => {
    const handel = async () => {
      const recipNumber: any = await generateReceiptNumber();
      if (recipNumber) {
        setReceiptNumber(recipNumber);
      }
    };
    handel();
  }, [isOpen]);

  // Load settings from localStorage
  useEffect(() => {
    const storedSettings = localStorage.getItem("settings");
    if (storedSettings) {
      try {
        setSettings(JSON.parse(storedSettings));
      } catch (error) {
        captureError(error, { component: 'SelectAdvancePaymentModalByMember', operation: 'parseSettings' });
      }
    }
  }, []);

  const requireCheckNumber = settings?.require_check_number !== false;

  // Early return must be AFTER all hooks
  if (!isOpen) return null;

  const handleConfirm = async () => {
    if (method === "cheque" && requireCheckNumber && !checkNumber.trim()) {
      alert("Check number is required.");
      return;
    }

    // If using a new card (not saved), create a PaymentMethod from the
    // CardElement first so we have an ID to pass to the backend.
    let newCardPaymentMethodId: string | null = null;
    if (method === "card" && (!savedCardLast4 || !useSavedCard)) {
      if (!stripe || !elements) {
        alert("Card processing not ready. Please try again.");
        return;
      }
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        alert("Please enter card details.");
        return;
      }
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
      });
      if (error) {
        alert(error.message || "Card error. Please check the details and try again.");
        return;
      }
      newCardPaymentMethodId = paymentMethod.id;
    }

    const payload: any = {
      method,
      amount,
      useSavedCard: method === "card" ? (useSavedCard && !!savedCardLast4) : false,
      newCardPaymentMethodId, // set when staff enters a new card
      useSavedBankAccount: method === "us_bank_account" ? useSavedBankAccount : false,
      note,
      receiptNumber,
      paymentDate,
      readerId: method === "card_reader" ? readerId : undefined,
      saveCardOnReader: method === "card_reader" ? saveCardOnReader : undefined,
      paidByMembershipUserId: askWhoPaid && paidBy ? paidBy : undefined,
    };

    if (method === "cheque") {
      payload.checkDetails = {
        checkNumber,
        bankName,
        checkDate,
      };
    }

    setSubmitting(true);
    try {
      const success = await onSubmit(payload);
      // Only close and reset if submission was successful
      if (success) {
        onClose();
        setBankName("");
        setCheckDate("");
        setCheckNumber("");
        setAmount(0);
        setAmountTouched(false);
        setNote("");
        setPaymentDate(getTodayDate());
      }
    } finally {
      setSubmitting(false);
    }
  };
  const handelClose = () => {
    onClose();
    setBankName("");
    setCheckDate("");
    setCheckNumber("");
    // Reset amount + touched flag so the next open pre-fills the (possibly
    // changed) amount due instead of showing a stale edited value.
    setAmount(0);
    setAmountTouched(false);
    setPaymentDate(getTodayDate()); // Reset to today's date
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center  bg-black bg-opacity-50 z-50">
      <div className={`rounded p-6 ${isDarkMode ? '' : 'shadow-lg'} w-[90%] max-w-md max-h-[90vh] overflow-auto ${getModalStyles(isDarkMode)}`}>
        <h3 className={`text-lg font-semibold mb-2 ${getPrimaryText(isDarkMode)}`}>Select Payment Method</h3>

        <div className="space-y-3">
          {/* Card */}
          <div className="flex items-center space-x-3">
            <input id="pay-method-card"
              type="radio"
              name="method"
              value="card"
              checked={method === "card"}
              onChange={() => setMethod("card")}
            />
            <label htmlFor="pay-method-card" className={getSecondaryText(isDarkMode)}>Card</label>
          </div>

          {/* Bank Account (ACH) */}
          <div className="flex items-center space-x-3">
            <input id="pay-method-us-bank-account"
              type="radio"
              name="method"
              value="us_bank_account"
              checked={method === "us_bank_account"}
              onChange={() => setMethod("us_bank_account")}
            />
            <label htmlFor="pay-method-us-bank-account" className={getSecondaryText(isDarkMode)}>Bank Account (ACH)</label>
          </div>

          {/* Card reader (Stripe Terminal) — only when the club has one */}
          {readers.length > 0 && (
            <div className="flex items-center space-x-3">
              <input id="pay-method-card-reader"
                type="radio"
                name="method"
                value="card_reader"
                checked={method === "card_reader"}
                onChange={() => setMethod("card_reader")}
              />
              <label htmlFor="pay-method-card-reader" className={getSecondaryText(isDarkMode)}>Card reader (tap at the desk)</label>
            </div>
          )}

          {/* Cash */}
          <div className="flex items-center space-x-3">
            <input id="pay-method-cash"
              type="radio"
              name="method"
              value="cash"
              checked={method === "cash"}
              onChange={() => setMethod("cash")}
            />
            <label htmlFor="pay-method-cash" className={getSecondaryText(isDarkMode)}>Cash</label>
          </div>

          {/* Check */}
          <div className="flex items-center space-x-3">
            <input id="pay-method-check"
              type="radio"
              name="method"
              value="check"
              checked={method === "cheque"}
              onChange={() => setMethod("cheque")}
            />
            <label htmlFor="pay-method-check" className={getSecondaryText(isDarkMode)}>Check</label>
          </div>

          {/* Account Credit */}
          <div className="flex items-center space-x-3">
            <input id="pay-method-account-credit"
              type="radio"
              name="method"
              value="account_credit"
              checked={method === "account_credit"}
              onChange={() => setMethod("account_credit")}
            />
            <label htmlFor="pay-method-account-credit" className={getSecondaryText(isDarkMode)}>Account Credit</label>
          </div>

          {/* Amount */}
          <div>
            <Input
              label="Amount"
              type="number"
              value={amount}
              onChange={(e) => {
                setAmountTouched(true);
                setAmount(parseFloat(e.target.value));
              }}
            />
          </div>

          {/* Payment Date */}
          <div>
            <label htmlFor="pay-payment-date" className={`block text-sm font-medium mb-1 ${getSecondaryText(isDarkMode)}`}>
              Payment Date {(method === "card" || method === "us_bank_account") && <span className="text-xs italic">(Today or future dates only)</span>}
            </label>
            <input id="pay-payment-date"
              type="date" autoComplete="off"
              className={`w-full px-3 py-2 rounded ${getInputStyles(isDarkMode)} ${method !== "card" && method !== "us_bank_account" ? "opacity-60 cursor-not-allowed" : ""}`}
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              disabled={method !== "card" && method !== "us_bank_account"}
              min={getTodayDate()}
            />
          </div>

          {/* Paid by — only on an account two or more people pay. */}
          {askWhoPaid && (
            <div>
              <Select
                label="Paid by"
                value={paidBy}
                onChange={(e) => {
                  setPaidByTouched(true);
                  setPaidBy(e.target.value);
                }}
                options={[
                  { value: "", label: "Not recorded" },
                  ...people.map((p) => ({ value: p.id, label: p.label })),
                ]}
                hint="Shown in billing history. The payment still goes to the account's one balance."
              />
            </div>
          )}

          {/* Receipt # — always shown so staff can record a paper-receipt
              cross-reference even on card / ACH / account-credit payments. */}
          <div>
            <Input
              label="Receipt #"
              type="text"
              value={receiptNumber}
              onChange={(e) => setReceiptNumber(e.target.value as any)}
              placeholder={
                method === "card" || method === "us_bank_account" || method === "account_credit" || method === "card_reader"
                  ? "Optional — for cross-reference with a paper receipt"
                  : ""
              }
            />
            {/* A warning only: a reused number never stops the payment. */}
            <ReceiptNumberWarning membershipId={membershipId} receiptNumber={receiptNumber} />
          </div>

          {/* Note */}
          <div>
            <Textarea
              label="Note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder='Optional — e.g. "mom paid", reference number, anything you want recorded'
            />
          </div>

          {/* Saved card or Add new card */}
          {method === "card" && (
            <div className="mt-2">
              {savedCards.length > 1 ? (
                <div>
                  <label className={`block text-sm font-medium mb-1 ${getSecondaryText(isDarkMode)}`}>
                    Select card
                  </label>
                  <select
                    value={useSavedCard ? (defaultPaymentMethodId || '') : 'new'}
                    onChange={(e) => {
                      if (e.target.value === 'new') {
                        setUseSavedCard(false);
                      } else {
                        setUseSavedCard(true);
                        onCardSelect?.(e.target.value);
                      }
                    }}
                    className={`w-full rounded-md px-3 py-2 text-sm border ${isDarkMode ? 'bg-gray-700 text-gray-200 border-gray-600' : 'bg-white text-gray-800 border-gray-300'}`}
                  >
                    {savedCards.map((card: any) => (
                      <option key={card.id} value={card.id}>
                        {card.card?.brand?.charAt(0).toUpperCase() + card.card?.brand?.slice(1) || 'Card'} •••• {card.card?.last4} {card.card?.exp_month}/{card.card?.exp_year}
                      </option>
                    ))}
                    <option value="new">Enter new card</option>
                  </select>
                </div>
              ) : savedCardLast4 ? (
                <label className={`inline-flex items-center ${getSecondaryText(isDarkMode)}`}>
                  <input
                    type="checkbox"
                    checked={useSavedCard}
                    onChange={() => setUseSavedCard(!useSavedCard)}
                  />
                  <span className="ml-2">
                    Use saved card (•••• {savedCardLast4})
                  </span>
                </label>
              ) : null}
              {/* Show inline card input when no saved card or user unchecks saved card */}
              {(!savedCardLast4 || !useSavedCard) && (
                <div className={savedCardLast4 ? "mt-3" : ""}>
                  {!savedCardLast4 && (
                    <p className={`text-sm mb-2 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>No saved card found. Enter card details below.</p>
                  )}
                  <div className={`border rounded-md p-3 ${isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-white'}`}>
                    <CardElement
                      options={{
                        style: {
                          base: {
                            fontSize: '16px',
                            color: isDarkMode ? '#f3f4f6' : '#1f2937',
                            '::placeholder': { color: isDarkMode ? '#9ca3af' : '#6b7280' },
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Saved bank account */}
          {method === "us_bank_account" && (
            <div className="mt-2">
              {savedBankAccountLast4 ? (
                <div className="space-y-3">
                  <label className={`inline-flex items-center ${getSecondaryText(isDarkMode)}`}>
                    <input
                      type="checkbox"
                      checked={useSavedBankAccount}
                      onChange={() => setUseSavedBankAccount(!useSavedBankAccount)}
                    />
                    <span className="ml-2">
                      Use saved bank account ({savedBankAccountName || 'Bank'} •••• {savedBankAccountLast4})
                    </span>
                  </label>
                  {/* ACH Processing Notice */}
                  <div className={`p-3 rounded-lg text-sm ${isDarkMode ? 'bg-yellow-900/30 text-yellow-200' : 'bg-yellow-50 text-yellow-800'} border ${isDarkMode ? 'border-yellow-700' : 'border-yellow-200'}`}>
                    <strong>Note:</strong> {ACH_PROCESSING_MESSAGE}
                  </div>
                </div>
              ) : (
                <p className={`text-sm ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                  No saved bank account found. Please add a bank account from Payment Settings first.
                </p>
              )}
            </div>
          )}

          {/* Check details */}
          {method === "cheque" && (
            <div className="space-y-2 mt-3">
              <div>
                <label htmlFor="pay-check-number" className={`block text-sm font-medium mb-1 ${getSecondaryText(isDarkMode)}`}>
                  Check Number{requireCheckNumber && <span className="text-red-500 ml-1">*</span>}
                </label>
                <input id="pay-check-number"
                  type="text"
                  className={`w-full px-3 py-2 rounded ${getInputStyles(isDarkMode)}`}
                  value={checkNumber}
                  onChange={(e) => setCheckNumber(e.target.value)}
                />
              </div>

              {/* <div>
                <Input
                  label="Bank Name"
                  type="text"
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                />
              </div> */}

              <div>
                <Input
                  label="Check Date"
                  type="date" autoComplete="off"
                  value={checkDate}
                  onChange={(e) => setCheckDate(e.target.value)}
                />
              </div>
            </div>
          )}
        </div>

        {method === "card_reader" && (
          <div className="mt-2">
            <ReaderPicker
              readers={readers}
              value={readerId}
              onChange={(id) => onReaderSelect?.(id)}
              canSaveCard
              saveCard={saveCardOnReader}
              onSaveCardChange={(v) => onSaveCardOnReaderChange?.(v)}
              disabled={submitting}
            />
          </div>
        )}

        <div className="flex justify-end gap-4 mt-6">
          <button
            className={`px-4 py-2 rounded border ${isDarkMode ? 'border-gray-800 text-gray-300 hover:bg-gray-700' : 'border-gray-600 text-gray-700 hover:bg-gray-100'}`}
            onClick={handelClose}
          >
            Cancel
          </button>
          {/* Show Confirm button for: cash, cheque, account_credit, any card (saved or new), or bank account with saved account */}
          {(method === "cash" ||
            method === "cheque" ||
            method === "account_credit" ||
            method === "card" ||
            (method === "card_reader" && !!readerId) ||
            (method === "us_bank_account" && savedBankAccountLast4 && useSavedBankAccount)) && (
            <button
              className="px-4 py-2 rounded bg-[var(--cc-action)] text-[var(--cc-action-text)] hover:bg-[var(--cc-action-hover)] transition-colors disabled:opacity-50"
              onClick={handleConfirm}
              disabled={submitting || (method === "cheque" && requireCheckNumber && !checkNumber.trim())}
            >
              {submitting ? "Processing..." : "Confirm"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Wrap in Stripe Elements so the CardElement for new cards works
const SelectAdvancePaymentMethodInMemberWrapper: React.FC<SelectAdvancePaymentMethodModalProps> = (props) => (
  <Elements stripe={getStripePromise()}>
    <SelectAdvancePaymentMethodInMember {...props} />
  </Elements>
);

export default SelectAdvancePaymentMethodInMemberWrapper;
